import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { API, graphqlOperation } from "aws-amplify";
import { CallFuncOperateFee } from "src/graphql/queries";

import {
  type FbaFeeContent,
  type ReferralFeePatternContent,
  type StorageFeeContent,
} from "@cielo-blu-team/amazon-fee-lib-v2";

import { setFeePatterns } from "src/common/feePatterns/feePatternsSlice";

/** LocalStorage キー */
const LOCAL_STORAGE_KEY = "feeflex.feePatterns";
/** キャッシュ有効期間（ミリ秒） 24時間 */
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

interface CachedPatterns {
  referralFee: ReferralFeePatternContent;
  fbaFee: FbaFeeContent;
  storageFee: StorageFeeContent;
  fetchedAt: number;
}

/**
 * 料率パターン定義を取得し、LocalStorage + Redux にキャッシュするフック。
 *
 * - 初回マウント時に LocalStorage を読み、有効期限内なら API を呼ばずに Redux に反映
 * - 期限切れ or 未取得なら GraphQL `CallFuncOperateFee(type: "get-fee-patterns")` を呼んで再取得
 */
export const useFeePatterns = (): {
  isLoading: boolean;
  hasError: boolean;
  refetch: () => Promise<void>;
} => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);

  /** API から取得して Redux + LocalStorage に保存 */
  const fetchAndStore = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setHasError(false);

    try {
      const { data: res }: any = await API.graphql(
        graphqlOperation(CallFuncOperateFee, { type: "get-fee-patterns" })
      );

      const parsed: any = JSON.parse(res.CallFuncOperateFee);

      if (parsed?.errorCode) {
        throw parsed;
      }

      const payload: CachedPatterns = {
        referralFee: parsed.referralFee,
        fbaFee: parsed.fbaFee,
        storageFee: parsed.storageFee,
        fetchedAt: Date.now(),
      };

      // LocalStorage に保存
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(payload));
      // Redux にディスパッチ
      dispatch(setFeePatterns(payload));
    } catch (error) {
      console.log(error);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    // LocalStorage を読み、有効期限内なら Redux にだけ反映して終了
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (raw !== null) {
      try {
        const cached: CachedPatterns = JSON.parse(raw);
        const isFresh =
          typeof cached.fetchedAt === "number" &&
          Date.now() - cached.fetchedAt < CACHE_TTL_MS;
        if (
          isFresh &&
          cached.referralFee &&
          cached.fbaFee &&
          cached.storageFee
        ) {
          dispatch(setFeePatterns(cached));
          return;
        }
      } catch {
        // パース失敗時はキャッシュを無視して再取得へ
      }
    }
    void fetchAndStore();
  }, [dispatch, fetchAndStore]);

  return {
    isLoading,
    hasError,
    refetch: fetchAndStore,
  };
};
